/**
 * theme.ts — theme detection and application.
 *
 * The active theme name is stored in stores/ui.ts (Zustand). This module
 * provides the mechanics of reading the system preference and writing the
 * active theme to <html data-theme="...">. CSS variables in styles/themes/
 * do the rest.
 */

export type ThemeName = 'light' | 'dark';

const noop = (): void => undefined;

const THEMES: readonly ThemeName[] = ['light', 'dark'] as const;

export function isThemeName(value: unknown): value is ThemeName {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

/**
 * Returns the user's system preference. Defaults to 'light' if the browser
 * does not support prefers-color-scheme.
 */
export function getSystemTheme(): ThemeName {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Applies the given theme by setting [data-theme] on <html>. Idempotent.
 */
export function applyTheme(theme: ThemeName): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

/**
 * Subscribes to system theme changes. Returns an unsubscribe function.
 */
export function subscribeSystemTheme(handler: (theme: ThemeName) => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return noop;
  }
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const listener = (e: MediaQueryListEvent) => handler(e.matches ? 'dark' : 'light');
  mq.addEventListener('change', listener);
  return () => mq.removeEventListener('change', listener);
}
